import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { getErpDate } from '@ca/common/types'
import Row, { textProps } from '@ui/elements/SectionRow/NestedRow'
import CollapsibleSectionRow from '@ui/elements/SectionRow/CollapsibleSectionRow'
import {
  OrderPaymentStatusBadge,
  useOrderDetailsPayment,
} from '@order/OrderHistory/OrderBadge'
import { isValidOrderPaymentTypeValue } from '@ca/common/types'
import { capitalize, isValidPaymentMethod } from '@ca/common'
import Typography from '@ui/typography'
import Price from '@/price/Price'
import {
  colorPaymentOverviewLine,
  orderPaymentOverviewText1,
  orderPaymentOverviewText2,
  orderPaymentTypeIcon,
} from '@order/OrderHistory/styles'

import { SectionProps } from '../types'

type Props = SectionProps & { openSignal?: number }

export default function PaymentsSection({ data, openSignal }: Props) {
  const { t } = useTranslation(['ui', 'common', 'order', 'types'])
  const header = 'order:details.sections.payments.header'

  const payments = data.payments
  const isEmpty = payments.length === 0

  const { total, paid, status } = useOrderDetailsPayment(data)

  return (
    <CollapsibleSectionRow
      openSignal={openSignal}
      empty={isEmpty}
      icon="credit-card-pay"
      title={t(`${header}.title`)}
      desc={t(`${header}.${isEmpty ? 'empty' : 'desc'}`)}
      content={
        <View className="flex flex-1 flex-col gap-2 pb-8 pl-24 pr-24">
          <View className="flex flex-row items-center justify-start">
            <Typography {...orderPaymentOverviewText1[status]}>
              <Price value={paid} showZero />
            </Typography>
            <Typography {...orderPaymentOverviewText2[status]}>
              {'  /  '}
              <Price value={total} showZero />
            </Typography>
          </View>
          <View className="flex h-1 w-full items-center justify-start overflow-hidden rounded-full bg-black/10">
            <View
              className="h-full self-start"
              style={{
                width:
                  status === 'partially' ? `${(paid / total) * 100}%` : '100%',
                backgroundColor: colorPaymentOverviewLine[status],
              }}
            />
          </View>
        </View>
      }
      highlightsLayers={{
        default: true,
        active: false,
        success: false,
        danger: true,
        attention: false,
        disabled: false,
      }}
    >
      {payments.map((payment, index) => {
        const type = isValidOrderPaymentTypeValue(payment.type)
          ? payment.type
          : 'unknown'

        const typeName = isValidOrderPaymentTypeValue(payment.type)
          ? t(`order:payment.type.values.${payment.type}`)
          : capitalize(payment.type)

        const methodName = isValidPaymentMethod(payment.method)
          ? t(`types:payment.method.${payment.method}`)
          : capitalize(payment.method)

        const title = `${methodName}  •  ${typeName}`

        const createdAt = format(
          getErpDate(payment.createdAt),
          'dd.MM.yyyy HH:mm:ss'
        )
        const updatedAt = format(
          getErpDate(payment.updatedAt),
          'dd.MM.yyyy HH:mm:ss'
        )

        const desc = `${createdAt}  •  ${updatedAt}`

        return (
          <Row
            key={`${payment.type}-${index}`}
            last={index === payments.length - 1}
            icon={orderPaymentTypeIcon[type]}
            title={title}
            desc={desc}
            body={
              <View className="flex flex-row items-center gap-4">
                <Typography {...textProps}>
                  <Price value={payment.amount} showZero />
                </Typography>
                <OrderPaymentStatusBadge status={payment.status} />
              </View>
            }
          />
        )
      })}
    </CollapsibleSectionRow>
  )
}
